import styles from "./Review.module.css";
/**
 * Review card grid — "Hear before Say". All per-card rules live in
 * ./reviewMachine (pure, unit-tested); this component only renders the machine
 * and bridges the shared practice `status` onto the active card.
 *
 * Items come from GET /student/review as { key, text, hasAudio, source }.
 * hasAudio === false -> word-only card: no speaker, mic live immediately.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { CorrectIcon, RecordIcon } from "../../resources/svgs";
import clsx from "clsx";
import { playSound } from "../../utils/playsound";
import Button from "../common/Button";
import { formatTime } from "../../utils/common";
import { soundLessons, Lessons } from "../../const/stages";
import lessonData from "../../const/lesson2.json";
import {
  initState,
  reviewReducer,
  canRecord,
  micEnabled,
  isCorrect,
  allDone,
  canExit,
  mustExit,
  EXIT_MAX_SECONDS,
} from "./reviewMachine";

export function getApplicableLessonID(lessonId) {
  const isSoundLesson =
    soundLessons.includes(lessonId) || soundLessons.includes(parseInt(lessonId));
  const id = isSoundLesson
    ? typeof lessonId === "number"
      ? lessonId - 1
      : parseInt(lessonId) - 1
    : lessonId;
  return id;
}

export default function Type1({
  items,
  lessonId,
  status,
  trialId,
  buttonsDisabled: defButtonsDisabled,
  onNext,
  onComplete,
  controls,
}) {
  const [state, dispatch] = useReducer(reviewReducer, items || [], initState);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  const exitedRef = useRef(false);
  // Synchronous lock: a second tap before React re-renders would otherwise see
  // stale state (canRecord still true) and start a 2nd recording. This blocks it.
  const recordingRef = useRef(false);

  const { onClick } = useMemo(
    () => controls.registerPractice(getApplicableLessonID(lessonId), null, "word"),
    []
  );

  // App-side display data (images / rich text) keyed by item key. The backend
  // gives us key + hasAudio; the visible text/images live in lesson2.json.
  const dictionary = useMemo(() => {
    const lesson = Lessons[getApplicableLessonID(lessonId)];
    return lessonData[lesson?.data]?.data || {};
  }, [lessonId]);

  // Play a card's sound by building the URL from the lesson's OWN audio template
  // (each lesson data carries the right path: `audios/…` for the alphabet
  // lessons, `flaudio/…` for the phonics/HFW lessons) plus its audioLessonId
  const playTheSound = useCallback(
    async (key) => {
      try {
        const applicable = getApplicableLessonID(lessonId);
        const lesson = Lessons[applicable];
        const tmpl = lessonData[lesson?.data]?.audio;
        if (tmpl) {
          const dictItem = lessonData[lesson?.data]?.data?.[key];
          const clip = dictItem?.clipId || dictItem?.ref || key; // some items map to another clip
          const audioId = lesson?.audioLessonId != null ? lesson.audioLessonId : applicable;
          const url = tmpl
            .replace(/\{\{lessonId\}\}/g, audioId)
            .replace(/\{\{key\}\}/g, String(clip).toLowerCase());
          await window.soundManager.playURL(url);
        } else {
          await playSound(`L${lessonId}_${key}`); // fallback for lessons without a template
        }
      } catch (e) {
        /* missing audio: do nothing */
      }
    },
    [lessonId]
  );

  // Elapsed timer starts once the intro finishes (buttons enabled).
  useEffect(() => {
    if (defButtonsDisabled) return undefined;
    if (startRef.current == null) startRef.current = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)),
      500
    );
    return () => clearInterval(id);
  }, [defButtonsDisabled]);

  const doExit = useCallback(async () => {
    if (exitedRef.current) return;
    exitedRef.current = true;
    try {
      if (onComplete) await onComplete();
    } finally {
      onNext();
    }
  }, [onComplete, onNext]);

  // C: pushed out at 5 minutes.
  useEffect(() => {
    if (mustExit(elapsed)) doExit();
  }, [elapsed, doExit]);

  // Bridge the shared practice status onto the active card only.
  const activeKey = state.activeKey;
  useEffect(() => {
    if (!activeKey) return;
    const st = state.cards[activeKey]?.status;
    if (status === "processing") {
      dispatch({ type: "RECORD_DONE", key: activeKey });
    } else if (status === "pass") {
      dispatch({ type: "RESULT", key: activeKey, correct: true });
    } else if (status === "fail") {
      dispatch({ type: "RESULT", key: activeKey, correct: false });
    } else if ((status == null || status === "started") && st === "recording") {
      // recording produced nothing (silence) — release the lock so they can retry
      dispatch({ type: "RECORD_CANCEL", key: activeKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const disabled = defButtonsDisabled;
  const exitReady = canExit(state, elapsed);

  return (
    <div className={styles.reviewPageFormat}>
      <div className={styles.gridList}>
        {(items || []).map((it) => {
          const key = it.key;
          const card = state.cards[key];
          const correct = isCorrect(state, key);
          const live = micEnabled(state, key);
          const recording = card?.status === "recording" || card?.status === "checking";
          const dictItem = dictionary[key];
          return (
            <div
              key={key}
              className={clsx(
                styles.soundItem,
                live && styles.listened,
                recording && styles.recording,
                correct && styles.success
              )}
            >
              <div
                className={styles.clickableSound}
                onClick={() => {
                  if (disabled || !it.hasAudio) return;
                  dispatch({ type: "HEARD", key });
                  playTheSound(key);
                }}
              >
                {dictItem?.imgs ? (
                  dictItem.imgs.map((src) => <img key={src} src={src} alt="" />)
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: dictItem?.text || it.text || key,
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1 }} />

              {/* speaker only when the card has audio */}
              {it.hasAudio && (
                <div
                  className={clsx(styles.soundButton, styles.playButton)}
                  onClick={() => {
                    if (disabled) return;
                    dispatch({ type: "HEARD", key });
                    playTheSound(key);
                  }}
                >
                  <svg viewBox="0 0 15 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.75 4.25C9.75 4.25 10.75 4.75 10.75 6C10.75 7.25 9.75 7.75 9.75 7.75M10.75 1.25C12.75 2.25 13.75 3.75 13.75 6C13.75 8.25 12.75 9.75 10.75 10.75M0.75 3.75V8.25H3.25L7.25 11.25V0.75L3.25 3.75H0.75Z"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}

              {/* result (green check) or mic (retryable, unlimited) */}
              {correct ? (
                <div className={clsx(styles.soundStatus, styles.success)}>
                  <CorrectIcon />
                </div>
              ) : (
                <div
                  className={clsx(
                    styles.soundButton,
                    // red only while actually recording; otherwise stays neutral
                    // (hearing the sound must NOT light the mic — the user taps it)
                    recording ? styles.recordButton : styles.micButton
                  )}
                  onClick={async () => {
                    if (disabled || recordingRef.current || !canRecord(state, key)) return;
                    recordingRef.current = true;
                    dispatch({ type: "RECORD_START", key });
                    try {
                      await onClick(key);
                    } finally {
                      recordingRef.current = false;
                    }
                  }}
                >
                  <RecordIcon />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.pageFooter}>
        <span className={styles.countDown}>
          {formatTime(Math.max(0, EXIT_MAX_SECONDS - elapsed))}
        </span>
        <Button
          isDisabled={disabled || !exitReady}
          onClick={doExit}
          paddingV="full"
          paddingH="full"
          title={allDone(state) ? "Done!" : "Go!"}
          icon={"right"}
          background={"green"}
          click={"inside"}
        />
      </div>
    </div>
  );
}
